import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { getApiUrl } from '../../config/api';
import PressureChart from './PressureChart';

interface ComponentData {
  score: number;
  raw: any;
  weight: number;
  available: boolean;
}

interface ComponentInterpretation {
  current: string;
  means: string;
  action: string;
}

interface SummaryData {
  narrative: string;
  action: string;
  action_text: string;
  action_color: string;
  confidence: number;
  key_drivers: { id: string; name: string; score: number }[];
  component_info: Record<string, { name: string; description: string; bullish: string; bearish: string }>;
  component_interpretations?: Record<string, ComponentInterpretation>;
}

interface PressureContext {
  price_usd?: number;
  change_24h_pct?: number;
  volume_24h_usd?: number;
  oi_usd?: number;
  funding_rate?: number;
  netflow_usd?: number;
  whale_net_usd?: number;
}

interface PressureScore {
  asset: string;
  ts: number;
  score_raw: number;
  score_smoothed: number;
  verdict: string;
  completeness: number;
  components: Record<string, ComponentData>;
  context?: PressureContext;
  summary?: SummaryData;
  stub?: boolean;
}

const VERDICT_COLORS: Record<string, string> = {
  'EXTREME BUYER PRESSURE': '#22c55e',
  'STRONG BUYER PRESSURE': '#4ade80',
  'BUYERS LEANING IN': '#86efac',
  'BALANCED': '#f59e0b',
  'SELLERS LEANING IN': '#fca5a5',
  'STRONG SELLER PRESSURE': '#f87171',
  'EXTREME SELLER PRESSURE': '#ef4444',
};

const ACTION_COLORS: Record<string, string> = {
  green: '#22c55e',
  amber: '#f59e0b',
  red: '#ef4444',
};

const COMPONENT_LABELS: Record<string, string> = {
  funding: 'Funding Rate', oi: 'Open Interest', momentum: 'Price Momentum 24h',
  cvd: 'Spot CVD 1h', ls_ratio: 'Long/Short Ratio', liquidations: 'Liquidations',
  netflow: 'Exchange Netflow', whale_bias: 'Whale Activity', ecosystem_heat: 'Ecosystem Heat',
};

function formatCompact(v: number): string {
  if (Math.abs(v) >= 1e9) return (v / 1e9).toFixed(1) + 'B';
  if (Math.abs(v) >= 1e6) return (v / 1e6).toFixed(1) + 'M';
  if (Math.abs(v) >= 1e3) return (v / 1e3).toFixed(1) + 'K';
  return v.toFixed(1);
}

// --- Tooltip component ---
const InfoTooltip: React.FC<{ content: React.ReactNode }> = ({ content }) => {
  const [show, setShow] = useState(false);
  return (
    <span className="relative inline-block ml-1">
      <span
        className="cursor-help text-viz-text-secondary/50 hover:text-viz-text-secondary text-xs border border-viz-text-secondary/30 rounded-full w-4 h-4 inline-flex items-center justify-center"
        onMouseEnter={() => setShow(true)}
        onMouseLeave={() => setShow(false)}
        onClick={() => setShow(!show)}
      >
        i
      </span>
      {show && (
        <div className="absolute z-50 bottom-6 left-1/2 -translate-x-1/2 w-72 bg-viz-bg-primary border border-viz-border rounded-lg p-3 text-xs text-viz-text-secondary shadow-xl">
          {content}
        </div>
      )}
    </span>
  );
};

// --- Tooltip content with current value, meaning, and action ---
const RichTooltip: React.FC<{
  interpretation?: ComponentInterpretation;
  description?: string;
}> = ({ interpretation, description }) => {
  if (!interpretation) return <>{description}</>;
  return (
    <div className="space-y-2">
      <div>
        <div className="text-[10px] uppercase tracking-wider text-viz-text-secondary/60 mb-0.5">Now</div>
        <div className="text-viz-text-primary">{interpretation.current}</div>
      </div>
      <div>
        <div className="text-[10px] uppercase tracking-wider text-viz-text-secondary/60 mb-0.5">Means</div>
        <div>{interpretation.means}</div>
      </div>
      <div>
        <div className="text-[10px] uppercase tracking-wider text-viz-text-secondary/60 mb-0.5">Action</div>
        <div>{interpretation.action}</div>
      </div>
      {description && (
        <div className="pt-2 border-t border-viz-text-secondary/20 text-viz-text-secondary/60">
          {description}
        </div>
      )}
    </div>
  );
};

// --- Score bar with tooltip ---
const ScoreBar: React.FC<{
  id: string;
  data: ComponentData;
  info?: { description: string };
  interpretation?: ComponentInterpretation;
}> = ({ id, data, info, interpretation }) => {
  const pct = Math.abs(data.score) / 100;
  const isPositive = data.score >= 0;
  const barColor = isPositive ? '#4ade80' : '#f87171';
  const label = COMPONENT_LABELS[id] || id;

  return (
    <div className="mb-3">
      <div className="flex justify-between text-sm mb-1">
        <span className="text-viz-text-secondary">
          {label}
          {(info || interpretation) && (
            <InfoTooltip
              content={<RichTooltip interpretation={interpretation} description={info?.description} />}
            />
          )}
        </span>
        <span className="font-mono" style={{ color: data.available ? barColor : '#666' }}>
          {data.score > 0 ? '+' : ''}{data.score.toFixed(1)}
          {!data.available && <span className="text-yellow-500 ml-1 text-xs">N/A</span>}
        </span>
      </div>
      <div className="h-2 bg-viz-bg-tertiary rounded-full overflow-hidden relative">
        <div className="absolute left-1/2 top-0 bottom-0 w-px bg-viz-text-secondary/30" />
        <div
          className="absolute top-0 bottom-0 rounded-full transition-all duration-500"
          style={{
            backgroundColor: barColor,
            width: `${pct * 50}%`,
            left: isPositive ? '50%' : `${50 - pct * 50}%`,
            opacity: data.available ? 1 : 0.3,
          }}
        />
      </div>
    </div>
  );
};


const PressureDashboard: React.FC = () => {
  const [score, setScore] = useState<PressureScore | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchScore = useCallback(async () => {
    try {
      const resp = await fetch(getApiUrl('/api/pressure/score/TON'), { credentials: 'include' });
      if (resp.ok) {
        setScore(await resp.json());
        setError(null);
      } else {
        setError(`API error: ${resp.status}`);
      }
    } catch (err) {
      setError('Failed to connect');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchScore();
    const interval = setInterval(fetchScore, 60_000);
    return () => clearInterval(interval);
  }, [fetchScore]);

  const ctx = score?.context;
  const summary = score?.summary;
  const verdictColor = score ? (VERDICT_COLORS[score.verdict] || '#f59e0b') : '#f59e0b';
  const actionColor = summary ? (ACTION_COLORS[summary.action_color] || '#f59e0b') : '#f59e0b';

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-viz-text-primary">Pressure Intelligence</h1>
          <p className="text-viz-text-secondary mt-1">TON Market Pressure Score</p>
          <div className="flex gap-3 mt-1">
            <Link to="/pressure/alerts" className="text-xs text-viz-primary hover:underline">Alerts</Link>
            <Link to="/pressure/backtest" className="text-xs text-viz-primary hover:underline">Backtest</Link>
          </div>
        </div>
        {ctx && ctx.price_usd ? (
          <div className="text-right">
            <div className="text-xl font-mono text-viz-text-primary">${ctx.price_usd.toFixed(4)}</div>
            <div className={`text-sm font-mono ${(ctx.change_24h_pct || 0) >= 0 ? 'text-green-400' : 'text-red-400'}`}>
              {(ctx.change_24h_pct || 0) >= 0 ? '+' : ''}{(ctx.change_24h_pct || 0).toFixed(2)}%
            </div>
          </div>
        ) : null}
      </div>

      {/* AI Summary + Recommendation */}
      {summary && !score?.stub && (
        <div className="glass-card p-5 mb-6 border-l-4" style={{ borderLeftColor: actionColor }}>
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1">
              <div className="text-sm text-viz-text-primary leading-relaxed"
                   dangerouslySetInnerHTML={{
                     __html: summary.narrative.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
                   }}
              />
            </div>
            <div className="flex-shrink-0 text-center min-w-[100px]">
              <div className="text-2xl font-bold font-mono" style={{ color: actionColor }}>
                {summary.action}
              </div>
              <div className="text-xs text-viz-text-secondary mt-1">
                Confidence: {summary.confidence}%
              </div>
            </div>
          </div>
          <div className="text-xs text-viz-text-secondary mt-2 pt-2 border-t border-viz-border">
            {summary.action_text}
          </div>
        </div>
      )}

      {/* Context strip */}
      {ctx && ctx.price_usd ? (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
          {[
            { label: 'Volume 24h', value: `$${formatCompact(ctx.volume_24h_usd || 0)}`, info: 'Total trading volume on Binance spot in the last 24 hours' },
            { label: 'Open Interest', value: `$${formatCompact(ctx.oi_usd || 0)}`, info: 'Total value of all open perpetual futures contracts' },
            { label: 'Funding Rate', value: `${((ctx.funding_rate || 0) * 100).toFixed(4)}%`, info: '8-hour funding rate. Positive = longs pay shorts' },
            { label: 'Completeness', value: `${((score?.completeness || 0) * 100).toFixed(0)}%`, info: 'Percentage of scoring components with fresh data' },
          ].map(({ label, value, info }) => (
            <div key={label} className="glass-card p-3 text-center">
              <div className="text-xs text-viz-text-secondary">
                {label}
                <InfoTooltip content={info} />
              </div>
              <div className="text-sm font-mono text-viz-text-primary mt-1">{value}</div>
            </div>
          ))}
        </div>
      ) : null}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* Score Hero */}
        <div className="glass-card p-8 text-center flex flex-col justify-center">
          {loading ? (
            <div className="text-viz-text-secondary">Loading...</div>
          ) : error ? (
            <div className="text-red-400 text-sm">{error}</div>
          ) : score ? (
            <>
              <div className="text-7xl font-bold font-mono mb-2" style={{ color: verdictColor }}>
                {score.score_smoothed > 0 ? '+' : ''}{score.score_smoothed.toFixed(1)}
              </div>
              <div className="text-lg font-semibold mb-2" style={{ color: verdictColor }}>
                {score.verdict}
              </div>
              <div className="text-xs text-viz-text-secondary">
                Raw: {score.score_raw.toFixed(1)} | Updated: {score.ts ? new Date(score.ts * 1000).toLocaleTimeString() : '—'}
              </div>
              {score.stub && (
                <div className="mt-3 text-xs text-yellow-500 border border-yellow-500/30 rounded px-3 py-1 inline-block mx-auto">
                  Waiting for data — engine warming up
                </div>
              )}
            </>
          ) : (
            <div className="text-viz-text-secondary">Score unavailable</div>
          )}
        </div>

        {/* Components with tooltips */}
        <div className="glass-card p-6">
          <h2 className="text-lg font-semibold text-viz-text-primary mb-4">
            Score Components
            <InfoTooltip content="Each component contributes to the composite Pressure Score based on its weight. Green = bullish signal, Red = bearish signal. The bar shows magnitude from center (0)." />
          </h2>
          {score && Object.keys(score.components).length > 0 ? (
            Object.entries(score.components)
              .sort(([, a], [, b]) => b.weight - a.weight)
              .map(([id, data]) => (
                <ScoreBar
                  key={id}
                  id={id}
                  data={data}
                  info={summary?.component_info?.[id]}
                  interpretation={summary?.component_interpretations?.[id]}
                />
              ))
          ) : (
            <div className="text-viz-text-secondary text-sm">
              {loading ? 'Loading...' : 'Waiting for first score tick (~60s)'}
            </div>
          )}
        </div>
      </div>

      {/* Live Chart with Pressure Score indicator */}
      <PressureChart />
    </div>
  );
};

export default PressureDashboard;
